import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ChatBubbleBottomCenterTextIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

interface Response {
  id: number;
  modul_id: number;
  modul_typ: string;
  modul_titel: string;
  antwort_data: any;
  antwort_text: string;
  beantwortet_am: string;
}

interface Recipient {
  id: number;
  empfaenger_name: string;
  empfaenger_email: string;
  Vorname: string;
  Name: string;
  Klasse: string;
  geoeffnet_am: string;
  beantwortet_am: string;
  antworten?: Response[];
}

interface ResponseDetailsModalProps {
  recipientId: number;
  elternbriefId: number;
  isOpen: boolean;
  onClose: () => void;
}

const ResponseDetailsModal: React.FC<ResponseDetailsModalProps> = ({
  recipientId,
  elternbriefId,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [recipient, setRecipient] = useState<Recipient | null>(null);

  useEffect(() => {
    if (isOpen && recipientId) {
      loadResponseDetails();
    }
  }, [isOpen, recipientId]);

  const loadResponseDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/massenmail/${elternbriefId}/recipient/${recipientId}/responses`);
      if (response.data.success) {
        setRecipient(response.data.data);
      }
    } catch (error: any) {
      console.error('Error loading response details:', error);
      toast.error('Fehler beim Laden der Antwortdetails');
    } finally {
      setLoading(false);
    }
  };

  const renderModuleResponse = (response: Response) => {
    switch (response.modul_typ) {
      case 'lesebestaetigung':
        return (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            <span>Gelesen und bestätigt</span>
          </div>
        );

      case 'umfrage':
        const antwortData = typeof response.antwort_data === 'string'
          ? JSON.parse(response.antwort_data)
          : response.antwort_data;

        const surveyEntries: Array<{
          frageId: number | string;
          frageText?: string | null;
          value: string | string[];
        }> = [];

        if (antwortData?.umfrage_antworten && Array.isArray(antwortData.umfrage_antworten)) {
          antwortData.umfrage_antworten.forEach((entry: any) => {
            const frageId = entry.frage_id ?? '?';
            const frageText = entry.frage_text || null;
            if (entry.antwort_text) {
              surveyEntries.push({
                frageId,
                frageText,
                value: entry.antwort_text
              });
            } else if (Array.isArray(entry.ausgewaehlte_optionen)) {
              surveyEntries.push({
                frageId,
                frageText,
                value: entry.ausgewaehlte_optionen
              });
            } else if (entry.bewertung) {
              surveyEntries.push({
                frageId,
                frageText,
                value: `Bewertung: ${entry.bewertung}`
              });
            }
          });
        } else if (antwortData?.antworten) {
          Object.entries(antwortData.antworten).forEach(([frageId, antwort]: [string, any]) => {
            surveyEntries.push({
              frageId,
              value: antwort
            });
          });
        }

        return (
          <div className="space-y-3">
            {surveyEntries.map((entry) => (
              <div key={entry.frageId} className="bg-gray-50 p-3 rounded-lg">
                <div className="font-medium text-gray-700 mb-2">
                  {entry.frageText || 'Frage'}
                </div>
                {Array.isArray(entry.value) ? (
                  <ul className="list-disc list-inside space-y-1">
                    {entry.value.map((item, idx) => (
                      <li key={idx} className="text-gray-900">{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-900">{entry.value}</p>
                )}
              </div>
            ))}

            {antwortData?.freitext && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="font-medium text-blue-700 mb-2 flex items-center gap-2">
                  <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />
                  Freitext-Antwort
                </div>
                <p className="text-gray-900 whitespace-pre-wrap">{antwortData.freitext}</p>
              </div>
            )}

            {surveyEntries.length === 0 && !antwortData?.freitext && (
              <div className="text-gray-500">Keine Umfrage-Antworten vorhanden</div>
            )}
          </div>
        );

      case 'texteingabe':
        return (
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="font-medium text-gray-700 mb-2 flex items-center gap-2">
              <DocumentTextIcon className="h-4 w-4" />
              Text-Antwort
            </div>
            <p className="text-gray-900 whitespace-pre-wrap">
              {response.antwort_text || response.antwort_data?.text || ''}
            </p>
          </div>
        );

      default:
        return (
          <div className="text-gray-500">
            Antworttyp: {response.modul_typ}
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Antwortdetails
                </h3>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Laden...</p>
              </div>
            ) : recipient ? (
              <div className="space-y-6">
                {/* Recipient Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">Schüler</div>
                      <div className="font-medium text-gray-900">
                        {recipient.Vorname} {recipient.Name}
                      </div>
                      <div className="text-sm text-gray-600">Klasse {recipient.Klasse}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Empfänger</div>
                      <div className="font-medium text-gray-900">
                        {recipient.empfaenger_name}
                      </div>
                      <div className="text-sm text-gray-600">{recipient.empfaenger_email}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-xs text-gray-600">Geöffnet</div>
                        <div className="text-sm font-medium text-gray-900">
                          {recipient.geoeffnet_am ? new Date(recipient.geoeffnet_am).toLocaleString('de-DE') : '-'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      <div>
                        <div className="text-xs text-gray-600">Beantwortet</div>
                        <div className="text-sm font-medium text-gray-900">
                          {recipient.beantwortet_am ? new Date(recipient.beantwortet_am).toLocaleString('de-DE') : '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Responses */}
                {recipient.antworten && recipient.antworten.length > 0 ? (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <ChartBarIcon className="h-5 w-5" />
                      Antworten auf Module
                    </h4>
                    
                    {recipient.antworten.map((response) => (
                      <div key={response.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-medium text-gray-900">
                            {response.modul_titel}
                          </div>
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                            {response.modul_typ === 'lesebestaetigung' ? 'Lesebestätigung' :
                             response.modul_typ === 'umfrage' ? 'Umfrage' :
                             response.modul_typ === 'texteingabe' ? 'Texteingabe' :
                             response.modul_typ}
                          </span>
                        </div>
                        
                        {renderModuleResponse(response)}
                        
                        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                          Beantwortet am: {new Date(response.beantwortet_am).toLocaleString('de-DE')}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Keine Antworten vorhanden
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Keine Daten verfügbar
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseDetailsModal;
